import React from "react";
import { Offcanvas } from "react-bootstrap";
import { useTranslate } from "../../hooks/LanguageProvider";
import { toTimestamp } from "../../utilities/functions";
import "./UserHistoryDrawer.css";

export interface UserHistoryItem {
  idlog: number;
  nom_user: string;
  prenom_user: string;
  operation_user: string;
  table_operation: string;
  type_operation: string;
  date_operation: string;
  info_operation: string;
}

type OperationTone = {
  className: string;
  icon: string;
};

export const getOperationTone = (operation?: string): OperationTone => {
  const value = (operation || "").toLowerCase();

  if (/(delete|deleted|remove|suppression|supprimer|supprime|supprim)/.test(value)) {
    return { className: "history-op-danger", icon: "las la-trash" };
  }

  if (/(add|create|adding|ajout|ajouter|creation|création)/.test(value)) {
    return { className: "history-op-success", icon: "las la-plus-circle" };
  }

  if (/(update|edit|modified|modifie|modifié|modifier)/.test(value)) {
    return { className: "history-op-warning", icon: "las la-edit" };
  }

  if (/(restore|restorer|restaur)/.test(value)) {
    return { className: "history-op-info", icon: "las la-undo" };
  }

  return { className: "history-op-neutral", icon: "las la-history" };
};

export const OperationBadge: React.FC<{ operation?: string }> = ({ operation }) => {
  const tone = getOperationTone(operation);

  return (
    <span className={`history-operation-badge ${tone.className}`}>
      <i className={tone.icon}></i>
      <span>{operation || "-"}</span>
    </span>
  );
};

const renderDetails = (details?: string) => {
  if (!details) return <span className="history-muted">-</span>;

  try {
    const parsed = JSON.parse(details);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return (
        <div className="history-details-list">
          {Object.entries(parsed).map(([key, value]) => (
            <div className="history-details-row" key={key}>
              <span>{key}</span>
              <strong>{String(value ?? "-")}</strong>
            </div>
          ))}
        </div>
      );
    }
  } catch {
    // Plain text details are displayed as-is below.
  }

  return <p className="history-details-text">{details}</p>;
};

interface UserHistoryDrawerProps {
  show: boolean;
  history: UserHistoryItem | null;
  onHide: () => void;
}

const UserHistoryDrawer: React.FC<UserHistoryDrawerProps> = ({ show, history, onHide }) => {
  const { translate } = useTranslate();
  const userName = history ? `${history.nom_user} ${history.prenom_user}` : "-";

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="history-drawer">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <i className="las la-history"></i> {translate("History details")}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {history && (
          <>
            <div className="history-drawer-summary">
              <div className="history-avatar">
                {history.nom_user?.[0]}
                {history.prenom_user?.[0]}
              </div>
              <div>
                <h5>{userName}</h5>
                <OperationBadge operation={history.operation_user} />
              </div>
            </div>

            <div className="history-drawer-section">
              <div className="history-info-line">
                <span>{translate("Module")}</span>
                <strong>{history.table_operation || "-"}</strong>
              </div>
              <div className="history-info-line">
                <span>{translate("Type")}</span>
                <strong>{history.type_operation || "-"}</strong>
              </div>
              <div className="history-info-line">
                <span>{translate("Date")}</span>
                <strong>{toTimestamp(history.date_operation)}</strong>
              </div>
            </div>

            <div className="history-drawer-section">
              <h6>{translate("Informations")}</h6>
              {renderDetails(history.info_operation)}
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default UserHistoryDrawer;
